import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TableCell,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Add as AddIcon, CloudDownload, Visibility, Search as SearchIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import AccountantLayout from '../components/AccountantLayout';
import PaginatedTable from '../components/PaginatedTable';

const DocumentsPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadDocuments();
  }, [page, rowsPerPage, searchQuery]);

  const loadDocuments = async () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      const mockDocuments = [
        { id: 1, name: 'Q2_Financial_Report.pdf', type: 'Report', uploadDate: '2026-06-30', size: '2.4 MB', client: 'Atlas Construction Co' },
        { id: 2, name: 'Tax_Declaration_2026.pdf', type: 'Tax', uploadDate: '2026-06-28', size: '1.8 MB', client: 'General' },
        { id: 3, name: 'Contract_Meridian.pdf', type: 'Contract', uploadDate: '2026-06-25', size: '850 KB', client: 'Meridian Logistics' },
        { id: 4, name: 'Invoice_Backup_June.xlsx', type: 'Backup', uploadDate: '2026-06-22', size: '3.2 MB', client: 'General' },
        { id: 5, name: 'Audit_Report_Q1.pdf', type: 'Audit', uploadDate: '2026-06-20', size: '4.5 MB', client: 'General' },
      ];
      setDocuments(mockDocuments);
      setTotalCount(mockDocuments.length);
      setLoading(false);
    }, 500);
  };

  const columns = [
    { id: 'name', label: t('document_name'), minWidth: 250 },
    { id: 'type', label: t('type'), minWidth: 120 },
    { id: 'client', label: t('client'), minWidth: 180 },
    { id: 'uploadDate', label: t('upload_date'), minWidth: 120 },
    { id: 'size', label: t('size'), minWidth: 100 },
    { id: 'actions', label: t('actions'), minWidth: 100, align: 'center' },
  ];

  const getTypeColor = (type) => {
    const colors = {
      Report: { bg: '#E3F2FD', color: '#1976D2' },
      Tax: { bg: '#FFF3E0', color: '#FF9800' },
      Contract: { bg: '#F3E5F5', color: '#9C27B0' },
      Backup: { bg: '#E0F7FA', color: '#00BCD4' },
      Audit: { bg: '#FFEBEE', color: '#F44336' },
    };
    return colors[type] || { bg: '#F5F5F5', color: '#666' };
  };

  const renderRow = (document) => {
    const typeColor = getTypeColor(document.type);
    return (
      <TableRow hover key={document.id}>
        <TableCell sx={{ fontWeight: 500 }}>{document.name}</TableCell>
        <TableCell>
          <Chip
            label={document.type}
            size="small"
            sx={{
              backgroundColor: typeColor.bg,
              color: typeColor.color,
              fontWeight: 500,
            }}
          />
        </TableCell>
        <TableCell>{document.client}</TableCell>
        <TableCell>{document.uploadDate}</TableCell>
        <TableCell>{document.size}</TableCell>
        <TableCell align="center">
          <IconButton size="small" color="primary">
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton size="small" color="primary">
            <CloudDownload fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <AccountantLayout>
      <Box>
        <Box className="accountant-page-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {t('accountant_documents')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('manage_client_documents')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
              },
            }}
          >
            {t('upload_document')}
          </Button>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder={t('search_documents_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: 'white',
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
        </Box>

        <PaginatedTable
          columns={columns}
          data={documents}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          emptyMessage={t('no_documents_found')}
          renderRow={renderRow}
        />
      </Box>
    </AccountantLayout>
  );
};

export default DocumentsPage;
